import { WIND_BARS_SCALING_FACTOR } from "@Components/forecast/hourly/WindItem";
import { BARS_LABEL_HEIGHT } from "@Components/forecast/label/BarsLabel";
import { BARS_MAX_HEIGHT } from "@Constants/bars";
import { Intervals } from "@Models/Interval";
import { scaleLinear } from "d3-scale";
import { curveMonotoneX, line } from "d3-shape";
import { useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

const LINE_WIDTH = 1;

type WindLineProps = {
  intervals: Intervals;
};

const WindLine = ({ intervals }: WindLineProps) => {
  const ref = useRef<SVGSVGElement>(null);
  const { width = 0 } = useResizeObserver({
    //@ts-expect-error it expects a ref to an element of type HTMLElement, but svg elements are of type SVGSVGElement
    ref,
  });
  const data: [number, number][] = intervals.map((hour, index) => [
    index,
    hour.FF_KMH,
  ]);

  // Get the min and max values of the data
  const xValues = data.map((d) => d[0]);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = 0;
  const yMax = BARS_MAX_HEIGHT / WIND_BARS_SCALING_FACTOR;

  // Set up the scales
  const height = BARS_MAX_HEIGHT;
  const xScale = scaleLinear().domain([xMin, xMax]).range([0, width]);
  const yScale = scaleLinear().domain([yMin, yMax]).range([height, 0]);

  // Set up the line generator
  const lineGenerator = line()
    .x((d) => xScale(d[0]))
    .y((d) => yScale(d[1]))
    .curve(curveMonotoneX);
  // Generate the line path data
  const linePath = lineGenerator(data) ?? undefined;

  return (
    <div
      aria-hidden
      style={{
        bottom: `${BARS_LABEL_HEIGHT}px`,
        height: `${BARS_MAX_HEIGHT}px`,
      }}
      className={
        "m:absolute m:z-2 m:flex m:w-full m:flex-row m:items-end m:gap-6"
      }>
      <svg
        ref={ref}
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none">
        <g transform={`translate(0, ${LINE_WIDTH / 2})`}>
          <path
            d={linePath}
            fill="none"
            strokeWidth={LINE_WIDTH}
            className="m:stroke-blue-950 m:dark:stroke-neutral-white"
          />
        </g>
      </svg>
    </div>
  );
};

export default WindLine;
